/*
=========================================================
* Material Kit 2 PRO React - v2.0.0
=========================================================
* Product Page: https://www.creative-tim.com/product/material-kit-pro-react
* Copyright 2021 CodeFox (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice
 shall be included in all copies or substantial portions of the Software. */

// next
import dynamic from "next/dynamic";
import Head from "next/head";
import Link from "@mui/material/Link";
import Script from "next/script";

// MUI
const Card = dynamic(() => import("@mui/material/Card"), {
  loading: () => null,
});
const Container = dynamic(() => import("@mui/material/Container"), {
  loading: () => null,
});
const Grid = dynamic(() => import("@mui/material/Grid"), {
  loading: () => null,
});

// PRO
const MDBox = dynamic(() => import("components/MDBox"), {
  loading: () => null,
});
const MDTypography = dynamic(() => import("components/MDTypography"), {
  loading: () => null,
});

// Parts
const DefaultFooter = dynamic(() => import("examples/Footers/DefaultFooter"), {
  loading: () => null,
});
const DefaultNavbar = dynamic(() => import("examples/DefaultNavbar"), {
  loading: () => null,
});

function Privacy() {
  return (
    <>
      <Head>
        <title>Privacy</title>
        <meta name="description" content="CodeFox values your privacy." />
        <meta property="og:title" content="CodeFox values your privacy" />
        <meta property="og:description" content="CodeFox's privacy policy" />
        <meta
          property="og:image"
          content="https://codefox.com/images/codefox.jpg"
        />
        <meta name="twitter:card" content="photo" />
        <meta name="twitter:title" content="CodeFox" />
        <meta
          name="twitter:description"
          content="Full Stack Developer in Huntington, NY"
        />
        <meta
          name="twitter:image"
          content="https://codefox.com/images/codefox.jpg"
        />
        <meta name="twitter:url" content="https://codefox.com" />
      </Head>
      <Script
        src={`https://www.googletagmanager.com/gtag/js?id=${process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS}`}
        strategy="afterInteractive"
      />
      <Script id="google-analytics" strategy="afterInteractive">
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){window.dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', '${process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS}');
        `}
      </Script>
      <DefaultNavbar transparent={false} light={false} />
      <MDBox component="section" pt={20} pb={12}>
        <Container>
          <Grid container justifyContent="center">
            <Grid item xs={12}>
              <Card>
                <MDBox
                  variant="gradient"
                  bgColor="dark"
                  borderRadius="lg"
                  coloredShadow="dark"
                  p={3}
                  mt={-3}
                  mx={2}
                >
                  <MDTypography variant="h3" color="white">
                    Terms and Conditions
                  </MDTypography>
                  <MDTypography variant="body2" color="white" opacity={0.8}>
                    Last modified: Apr 2, 2024
                  </MDTypography>
                </MDBox>
                <MDBox pb={6} px={6}>
                  <MDTypography variant="h5" mt={6} mb={3}>
                    Introduction
                  </MDTypography>
                  <MDTypography variant="body2" color="text">
                    If you have additional questions or require more information
                    about our Terms and Conditions, do not hesitate to contact
                    us through our <Link href="/#contact">contact form</Link>
                    <br />
                    <br />
                    These terms and conditions apply only to online activities
                    and is valid for visitors to our website with regards to
                    their use and activities on the{" "}
                    <MDTypography
                      variant="body2"
                      color="dark"
                      component="a"
                      href="https://codefox.com"
                      target="_blank"
                      rel="noreferrer"
                      sx={{
                        "&:hover": {
                          color: ({ palette: { info } }) => info.main,
                        },
                      }}
                    >
                      CodeFox
                    </MDTypography>{" "}
                    website. These terms and conditions do not necessarily apply
                    to activity offline or via channels other than this website.
                  </MDTypography>
                  <MDTypography variant="h5" mt={6} mb={3}>
                    Description of what we sell
                  </MDTypography>
                  <MDTypography variant="body2" color="text" mb={2}>
                    We sell web design, web development and app development,
                    routing, internet security and software services. The prices
                    for each is listed in USD in our written agreement that is
                    signed by us and you. No purchases are available online.
                  </MDTypography>
                  <MDTypography variant="h5" mt={6} mb={3}>
                    Refund policy
                  </MDTypography>
                  <MDTypography variant="body2" color="text" mb={2}>
                    There are no refunds.
                  </MDTypography>
                  <MDTypography variant="h5" mt={6} mb={3}>
                    Delivery policy
                  </MDTypography>
                  <MDTypography variant="body2" color="text" mb={2}>
                    The product of our services are made available online when
                    they become available or we otherwise discuss.
                  </MDTypography>
                  <MDTypography variant="h5" mt={6} mb={3}>
                    Return policy
                  </MDTypography>
                  <MDTypography variant="body2" color="text" mb={2}>
                    There are no returns.
                  </MDTypography>
                  <MDTypography variant="h5" mt={6} mb={3}>
                    Cancellation policy
                  </MDTypography>
                  <MDTypography variant="body2" color="text" mb={2}>
                    Subscriptions can be cancelled by following the instructions
                    in our written agreement.
                  </MDTypography>
                  <MDTypography variant="h5" mt={6} mb={3}>
                    Legal or export restrictions applicable to us
                  </MDTypography>
                  <MDTypography variant="body2" color="text" mb={2}>
                    None.
                  </MDTypography>
                  <MDTypography variant="h5" mt={6} mb={3}>
                    Privacy
                  </MDTypography>
                  <MDTypography variant="body2" color="text" mb={2}>
                    Our privacy policy is available at{" "}
                    <MDTypography
                      variant="body2"
                      color="dark"
                      component="a"
                      href="https://codefox.com/privacy"
                      target="_blank"
                      rel="noreferrer"
                      sx={{
                        "&:hover": {
                          color: ({ palette: { info } }) => info.main,
                        },
                      }}
                    >
                      https://codefox.com/privacy
                    </MDTypography>{" "}
                  </MDTypography>
                  <MDTypography variant="h5" mt={6} mb={3}>
                    Business Address
                  </MDTypography>
                  <MDTypography variant="body2" color="text" mb={2}>
                    Our business address is provided in the written agreement we
                    enter for services.
                  </MDTypography>
                  <MDTypography variant="h5" mt={6} mb={3}>
                    Promotions
                  </MDTypography>
                  <MDTypography variant="body2" color="text" mb={2}>
                    There are no current promotions.
                  </MDTypography>
                  <MDTypography variant="h5" mt={6} mb={3}>
                    Security of website and Customer Payment Information
                  </MDTypography>
                  <MDTypography variant="body2" color="text" mb={2}>
                    We do not collect payments on this website. We do not store
                    customer payment information.
                  </MDTypography>
                  <MDTypography variant="h5" mt={6} mb={3}>
                    The credit cards we accept
                  </MDTypography>
                  <MDTypography variant="body2" color="text" mb={2}>
                    We accept all major credit cards including Visa and
                    Mastercard.
                  </MDTypography>
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </MDBox>
      <MDBox pt={6} px={1} mt={6}>
        <DefaultFooter />
      </MDBox>
    </>
  );
}

export default Privacy;
